import { PageHeader } from '@/shared/components/ui/PageHeader';
// BizPlus parity Phase 3.2 — incoming lead inbox.
//
// Marketplace feeds carry duplicates, test entries and junk, so records land in
// staging and are triaged here rather than becoming CRM leads automatically.
// "Move" promotes to a real Lead (matching an existing contact by phone);
// "Discard" marks junk without deleting it, so the same record cannot be
// re-imported by the next poll.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Inbox, ArrowRight, Trash2, RefreshCw, CheckCheck, ExternalLink, AlertTriangle, UserPlus, UserMinus, Search,
} from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useAuthStore } from '@/shared/stores/auth.store';
import { useSalesAssignees } from '@/shared/hooks/useSalesAssignees';
import { cn, localToday } from '@/shared/utils/utils';
import { MultiSelect } from '@/shared/components/ui/MultiSelect';

interface StagedLead {
  _id: string;
  provider: string;
  providerRecordId: string;
  createdAt: string;
  isMoved: boolean;
  discarded: boolean;
  movedToId?: string | null;
  // Self-claim — once set, every non-admin except this user stops seeing the
  // row at all (server-side filter); admins keep seeing everything and the
  // name here so they can reassign a claim someone is sitting on.
  assignedTo?: { _id: string; display_name?: string } | null;
  normalized: {
    name: string; company: string; phone: string; altPhone: string;
    email: string; city: string; state: string; address: string;
    productInterest: string; message: string; receivedAt: string | null;
  };
  raw?: Record<string, unknown>;
  /** Lead Segregation — from IndiaMART's own QUERY_TYPE (server: shared/integrations/lead-category.js). */
  category?: { key: string; label: string; detail: string };
  /** A customer the CRM already knows (same phone) — shown before assigning. */
  existing?: {
    records: number;
    open: number;
    contact: boolean;
    latest: { id: string; name: string; ownerName: string; stage: string; createdAt?: string } | null;
  } | null;
}

const STAGE_LABEL: Record<string, string> = {
  new_lead: 'New lead', contacted: 'Contacted', follow_up: 'Follow-up', qualified: 'Qualified', sample_sent: 'Sample sent',
  quotation_sent: 'Quotation sent', negotiation: 'Negotiation', closed_won: 'Won', closed_lost: 'Lost',
};
const stageLabel = (s?: string) => (s ? STAGE_LABEL[s] || s.replace(/_/g, ' ') : '');

// Lead Segregation tabs: IndiaMART Call / Web / BuyLead, and everything else.
const LEAD_CATEGORY_TABS = [
  { key: 'im_call', label: 'Call Enquiries' },
  { key: 'im_web', label: 'Web Enquiries' },
  { key: 'im_buylead', label: 'BuyLead' },
] as const;
const CATEGORY_TONE: Record<string, string> = {
  im_call: 'bg-success/10 text-success-text border-success/25',
  im_web: 'bg-info/10 text-info-text border-info/25',
  im_buylead: 'bg-warning/10 text-warning-text border-warning/25',
  other: 'bg-slate-50 text-slate-600 border-slate-200',
};

interface ProviderItem {
  provider: string;
  label: string;
  delivery: string;
  isActive: boolean;
  pendingLeads: number;
  lastSyncAt: string | null;
  lastError: string;
}

const STATUSES = [
  { key: 'pending', label: 'To review' },
  { key: 'moved', label: 'Moved' },
  { key: 'discarded', label: 'Discarded' },
] as const;

export function LeadInboxPage() {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const [status, setStatus] = useState<'pending' | 'moved' | 'discarded'>('pending');
  const [provider, setProvider] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  // Who the selected enquiries go to. Blank = the old behaviour (whoever
  // presses Move, or the source's default assignee).
  const [assignTo, setAssignTo] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  // Search as you type, without a request per keystroke.
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 350); return () => clearTimeout(t); }, [search]);
  const { assignees } = useSalesAssignees();

  // Received-date range (whole days). Presets fill From/To; editing either
  // date by hand makes it a custom range.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    setSelected(new Set());
    const now = new Date();
    const shift = (days: number) => { const d = new Date(now); d.setDate(d.getDate() + days); return localToday(d); };
    const ranges: Record<string, [string, string]> = {
      today: [localToday(now), localToday(now)],
      yesterday: [shift(-1), shift(-1)],
      last7: [shift(-6), localToday(now)],
      this_month: [localToday(new Date(now.getFullYear(), now.getMonth(), 1)), localToday(now)],
    };
    const r = ranges[preset];
    setFromDate(r ? r[0] : '');
    setToDate(r ? r[1] : '');
  };

  const providers = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => (await api.get<{ items: ProviderItem[] }>('/api/integrations')).data,
  });

  const leads = useQuery({
    queryKey: ['integration-leads', status, provider, q, assignedFilter, fromDate, toDate, categoryFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ status });
      if (provider) p.set('provider', provider);
      if (q) p.set('q', q);
      if (assignedFilter) p.set('assignedTo', assignedFilter);
      if (categoryFilter) p.set('category', categoryFilter);
      if (fromDate) p.set('fromDate', fromDate);
      if (toDate) p.set('toDate', toDate);
      return (await api.get<{
        items: StagedLead[]; total: number; isAdmin?: boolean;
        assigneeCounts?: { id: string | null; name: string; count: number }[]; countTotal?: number;
        categoryCounts?: Record<string, number>;
      }>(`/api/integrations/leads?${p}`)).data;
    },
    placeholderData: (prev) => prev,
  });
  const pickAssignee = (v: string) => { setAssignedFilter(v); setSelected(new Set()); };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['integration-leads'] });
    qc.invalidateQueries({ queryKey: ['integrations'] });
    setSelected(new Set());
  };

  const move = useMutation({
    mutationFn: async ({ id, followupDate }: { id: string; followupDate?: string }) =>
      (await api.post(`/api/integrations/leads/${id}/move`, followupDate ? { followupDate } : {})).data,
    onSuccess: () => { refresh(); toast.success('Lead created.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not create the lead.')),
  });

  const bulkMove = useMutation({
    mutationFn: async ({ ids, to, followupDate }: { ids: string[]; to: string[]; followupDate?: string }) =>
      (await api.post('/api/integrations/leads/bulk-move', {
        ids, ...(to.length ? { assignTo: to } : {}), ...(followupDate ? { followupDate } : {}),
      })).data,
    onSuccess: (d: any, v) => {
      refresh();
      setAssignTo([]);
      const nameOf = (id: string) => assignees.find((u) => u.id === id)?.display_name || 'user';
      const who = v.to.map(nameOf).join(', ');
      // What each BDM actually received — the round-robin result, not just the intent.
      const split = Object.entries((d.distribution || {}) as Record<string, number>)
        .map(([id, n]) => `${nameOf(id)} ${n}`).join(', ');
      if (d.failed?.length) toast.error(`${d.moved} moved${split ? ` (${split})` : ''}, ${d.failed.length} failed: ${d.failed[0]?.error || ''}`);
      else toast.success(`${d.moved} lead(s) moved to the pipeline${v.to.length > 1 ? ` — ${split || `distributed equally across ${who}`}` : who ? ` for ${who}` : ''}.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Bulk move failed.')),
  });

  const bulkAssign = useMutation({
    mutationFn: async ({ ids, to }: { ids: string[]; to: string[] }) =>
      (await api.post('/api/integrations/leads/bulk-assign', { ids, assignTo: to })).data,
    onSuccess: (d: any, v) => {
      refresh();
      setAssignTo([]);
      const who = v.to.map((id) => assignees.find((u) => u.id === id)?.display_name || 'user').join(', ');
      if (d.failed?.length) toast.error(`${d.assigned} assigned, ${d.failed.length} failed: ${d.failed[0]?.error || ''}`);
      else toast.success(`${d.assigned} lead(s) assigned${v.to.length > 1 ? ` (distributed equally across ${who})` : who ? ` to ${who}` : ''}.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Bulk assign failed.')),
  });

  const bulkUnassign = useMutation({
    mutationFn: async (ids: string[]) =>
      (await api.post('/api/integrations/leads/bulk-unassign', { ids })).data,
    onSuccess: (d: any) => {
      refresh();
      toast.success(`${d.unassigned || 0} lead(s) released back to the shared queue.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Bulk release failed.')),
  });

  // No follow-up date prompt on Move (tracker item 97, 2026-10-23): an
  // IndiaMART lead's follow-up is set on its Sales Pipeline record, which cannot
  // move to its next stage without one (enforced server-side).
  const moveOne = (r: { _id: string }) => move.mutate({ id: r._id });

  const discard = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/integrations/leads/${id}/discard`)).data,
    onSuccess: () => { refresh(); toast.success('Marked as junk.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not discard.')),
  });

  const assign = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/integrations/leads/${id}/assign`)).data,
    onSuccess: () => { refresh(); toast.success('Assigned to you.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not assign.')),
  });

  const unassign = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/integrations/leads/${id}/unassign`)).data,
    onSuccess: () => { refresh(); toast.success('Released back to the shared queue.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not release.')),
  });

  const syncNow = useMutation({
    mutationFn: async (key: string) => (await api.post(`/api/integrations/leads/poll/${key}`)).data,
    onSuccess: (d: any) => {
      refresh();
      const hm = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
      // The provider only allows one check every few minutes (IndiaMART: 5) —
      // say when it was last checked and when the next check can run.
      if (d.skipped) toast(`Checked at ${hm(d.lastCheckedAt)} — IndiaMART allows one check every 5 minutes. Next check possible at ${hm(d.nextPossibleAt)}.`, { icon: '⏱️', duration: 7000 });
      else if (d.ok) toast.success(d.inserted ? `Synced — ${d.inserted} new lead(s).` : 'Synced — no new leads right now.');
      else toast.error(`Sync failed: ${d.error}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Sync failed.')),
  });

  // Only polled providers can be synced on demand; a webhook provider has
  // nothing to pull, so offering the button would be misleading.
  const pollable = useMemo(
    () => (providers.data?.items ?? []).filter((p) => p.delivery === 'poll' && p.isActive),
    [providers.data],
  );
  const withPending = useMemo(
    () => (providers.data?.items ?? []).filter((p) => p.pendingLeads > 0),
    [providers.data],
  );

  const rows = leads.data?.items ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r._id));

  const toggle = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="space-y-4 pb-16">
      <PageHeader
        icon={Inbox}
        eyebrow="Marketing"
        title="Incoming Leads"
        description={<>Records received from IndiaMART, JustDial, TradeIndia, Facebook and your website.
            Nothing becomes a CRM lead until you move it here.</>}
        actions={
          <><div className="flex flex-wrap gap-2">
                  {pollable.map((p) => (
                    <Button key={p.provider} size="sm" variant="secondary"
                      disabled={syncNow.isPending}
                      onClick={() => syncNow.mutate(p.provider)}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync {p.label}
                    </Button>
                  ))}
                </div></>
        }
      />

      {withPending.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {withPending.map((p) => (
            <button key={p.provider} type="button"
              onClick={() => { setProvider(p.provider); setStatus('pending'); }}
              className="rounded-full border border-outline bg-surface px-3 py-1 text-xs hover:border-primary">
              {p.label} <span className="font-semibold text-info-text">{p.pendingLeads}</span>
            </button>
          ))}
        </div>
      )}

      {(providers.data?.items ?? []).some((p) => p.lastError) && (
        <Card><CardBody className="flex items-start gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning-text" />
          <div className="space-y-0.5">
            {(providers.data?.items ?? []).filter((p) => p.lastError).map((p) => (
              <p key={p.provider}><span className="font-medium">{p.label}:</span>{' '}
                <span className="text-muted-foreground">{p.lastError}</span></p>
            ))}
          </div>
        </CardBody></Card>
      )}

      <Card>
        <CardHeader
          title={`${STATUSES.find((s) => s.key === status)?.label} (${leads.data?.total ?? 0})`}
          action={
            <div className="flex flex-wrap items-center gap-2">
              {STATUSES.map((s) => (
                <Button key={s.key} size="sm" variant={status === s.key ? 'secondary' : 'ghost'}
                  onClick={() => { setStatus(s.key); setSelected(new Set()); }}>
                  {s.label}
                </Button>
              ))}
            </div>
          }
        />

        {/* ── Filters: their own row, so the search box is never squeezed out
            of a narrow header. ─────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 border-t border-outline/60 px-4 py-3">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="search"
              aria-label="Search incoming leads"
              placeholder="Search name, company, phone, email, city, product…"
              className="h-9 w-full rounded border border-outline bg-surface pl-8 pr-2 text-sm"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelected(new Set()); }}
            />
          </div>
          <select
            aria-label="Received date"
            className="h-9 rounded border border-outline bg-surface px-2 text-xs"
            value={datePreset} onChange={(e) => applyPreset(e.target.value)}
          >
            <option value="">Any date</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="this_month">This month</option>
            {datePreset === 'custom' && <option value="custom">Custom range</option>}
          </select>
          <input type="date" aria-label="From date" className="h-9 rounded border border-outline bg-surface px-2 text-xs"
            value={fromDate} max={toDate || undefined}
            onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); setSelected(new Set()); }} />
          <span className="text-xs text-on-surface-variant">to</span>
          <input type="date" aria-label="To date" className="h-9 rounded border border-outline bg-surface px-2 text-xs"
            value={toDate} min={fromDate || undefined}
            onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); setSelected(new Set()); }} />
          <select
            aria-label="Provider"
            className="h-9 rounded border border-outline bg-surface px-2 text-xs"
            value={provider} onChange={(e) => { setProvider(e.target.value); setSelected(new Set()); }}
          >
            <option value="">All providers</option>
            {(providers.data?.items ?? []).map((p) => (
              <option key={p.provider} value={p.provider}>{p.label}</option>
            ))}
          </select>
          {(search || fromDate || toDate || provider || assignedFilter) && (
            <Button size="sm" variant="ghost" onClick={() => { setSearch(''); applyPreset(''); setProvider(''); pickAssignee(''); setCategoryFilter(''); }}>
              Clear filters
            </Button>
          )}
        </div>

        {/* ── Lead Segregation: IndiaMART Call / Web / BuyLead, and Other. ── */}
        <div role="tablist" aria-label="Lead category" className="flex flex-wrap items-center gap-1.5 border-t border-outline/60 px-4 py-2">
          {(() => {
            const cc = leads.data?.categoryCounts ?? {};
            const all = Object.values(cc).reduce((n, v) => n + v, 0);
            const tab = (value: string, label: string, count: number | undefined) => (
              <button key={value || 'all'} type="button" role="tab" aria-selected={categoryFilter === value}
                onClick={() => { setCategoryFilter(value); setSelected(new Set()); }}
                className={cn('rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                  categoryFilter === value ? 'border-primary bg-primary text-on-primary' : 'border-outline text-on-surface-variant hover:border-primary hover:text-primary')}>
                {label}{count !== undefined && <span className="ml-1.5 opacity-80">{count}</span>}
              </button>
            );
            return (
              <>
                {tab('', 'All leads', leads.data?.categoryCounts ? all : undefined)}
                <span className="ml-2 mr-0.5 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">IndiaMART</span>
                {LEAD_CATEGORY_TABS.map((c) => tab(c.key, c.label, cc[c.key]))}
                <span className="ml-2 mr-0.5 h-4 border-l border-outline" />
                {tab('other', 'Other Incoming Leads', cc.other)}
              </>
            );
          })()}
        </div>

        {/* ── One tab per assignee, with counts for the filters above. ──── */}
        <div role="tablist" aria-label="Assigned to" className="flex flex-wrap items-center gap-1.5 border-t border-outline/60 px-4 py-2">
          {(() => {
            const counts = leads.data?.assigneeCounts ?? [];
            const tab = (value: string, label: string, count: number | undefined) => (
              <button key={value || 'all'} type="button" role="tab" aria-selected={assignedFilter === value}
                onClick={() => pickAssignee(value)}
                className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  assignedFilter === value ? 'border-primary bg-primary text-on-primary' : 'border-outline text-on-surface-variant hover:border-primary hover:text-primary')}>
                {label}{count !== undefined && <span className="ml-1.5 opacity-80">{count}</span>}
              </button>
            );
            return [
              tab('', 'All', leads.data?.countTotal),
              tab('unassigned', 'Unassigned', counts.find((c) => c.id === null)?.count ?? 0),
              ...counts.filter((c) => c.id).map((c) => tab(
                leads.data?.isAdmin ? String(c.id) : 'me',
                c.id === currentUser?.id ? `${c.name} (you)` : c.name,
                c.count,
              )),
            ];
          })()}
        </div>

        {status === 'pending' && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-outline/60 bg-primary/5 px-4 py-2">
            <span className="text-xs font-medium">{selected.size} selected →</span>
            {leads.data?.isAdmin ? (
              // Multiple BDMs: distribute selected leads equally across them (round-robin)
              <div className="w-72" aria-label="Assign selected leads to">
                <MultiSelect
                  options={assignees.map((u) => ({ value: u.id, label: u.display_name }))}
                  value={assignTo}
                  onChange={setAssignTo}
                  placeholder="Choose one or more BDMs to distribute…"
                />
              </div>
            ) : (
              <span className="text-xs font-semibold text-primary">Assigned to you</span>
            )}
            <Button size="sm" variant="ghost"
              disabled={bulkAssign.isPending || (!!leads.data?.isAdmin && !assignTo.length)}
              title={leads.data?.isAdmin && !assignTo.length ? 'Choose the BDM(s) first' : undefined}
              onClick={() => {
                const names = assignTo.map((id) => assignees.find((u) => u.id === id)?.display_name || 'user');
                const who = !names.length ? 'you'
                  : names.length === 1 ? names[0]
                  : `distributed equally across ${names.join(', ')}`;
                if (window.confirm(`Assign ${selected.size} lead(s) in inbox, ${names.length > 1 ? who : `assigned to ${who}`}?`)) {
                  bulkAssign.mutate({ ids: [...selected], to: assignTo });
                }
              }}>
              <UserPlus className="h-4 w-4 mr-1" /> Keep in inbox for them
            </Button>
            {(() => {
              const selectedRows = rows.filter((r) => selected.has(r._id));
              const allSelectedAlreadyAssigned = selectedRows.length > 0 && selectedRows.every((r) => !!r.assignedTo);
              const canMove = !bulkMove.isPending && (!leads.data?.isAdmin || assignTo.length > 0 || allSelectedAlreadyAssigned);

              return (
                <Button size="sm"
                  disabled={!canMove}
                  title={leads.data?.isAdmin && !assignTo.length && !allSelectedAlreadyAssigned ? 'Choose the BDM(s) first' : undefined}
                  onClick={() => {
                    const names = assignTo.map((id) => assignees.find((u) => u.id === id)?.display_name || 'user');
                    const who = assignTo.length > 1
                      ? `distributed equally across ${names.join(', ')}`
                      : assignTo.length === 1
                      ? `assigned to ${names[0]}`
                      : allSelectedAlreadyAssigned
                      ? 'keeping their currently assigned BDMs'
                      : 'assigned to you';
                    const known = rows.filter((r) => selected.has(r._id) && r.existing).length;
                    const note = known ? `\n\n${known} of them are existing customers — each still becomes a NEW lead for the chosen person; their earlier records are not changed.` : '';
                    if (window.confirm(`Assign ${selected.size} lead(s) to the Sales Pipeline, ${who}?\n\nEach shows as a New lead; product and follow-up are asked for before it can move on.${note}`)) {
                      bulkMove.mutate({ ids: [...selected], to: assignTo });
                    }
                  }}>
                  <CheckCheck className="h-4 w-4 mr-1" /> {leads.data?.isAdmin ? `Assign ${selected.size} to Sales Pipeline` : `Move ${selected.size} to my pipeline`}
                </Button>
              );
            })()}
            {(leads.data?.isAdmin || rows.some((r) => selected.has(r._id) && r.assignedTo?._id === currentUser?.id)) && (
              <Button size="sm" variant="ghost" disabled={bulkUnassign.isPending}
                title="Release claim and return to shared unassigned pool"
                onClick={() => {
                  if (window.confirm(`Release ${selected.size} lead(s) back to Unassigned?`)) {
                    bulkUnassign.mutate([...selected]);
                  }
                }}>
                <UserMinus className="h-4 w-4 mr-1" /> Release
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        )}
        <CardBody className="p-0">
          {leads.isLoading ? (
            <div className="p-6"><Skeleton className="h-48" /></div>
          ) : !rows.length ? (
            <div className="p-6">
              <EmptyState
                title={status === 'pending' ? 'Nothing waiting' : `No ${status} records`}
                description={status === 'pending'
                  ? 'New enquiries from your connected sources will appear here.'
                  : 'Records you move or discard are kept here for reference.'}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase">
                  <tr>
                    {status === 'pending' && (
                      <th className="w-10 px-4 py-3">
                        <input type="checkbox" checked={allSelected}
                          onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r._id)) : new Set())} />
                      </th>
                    )}
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Interest</th>
                    <th className="px-4 py-3">Received</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    // Fragment carries the key: keys on the children inside an
                    // unkeyed fragment do not satisfy React's list requirement,
                    // and without it React can mis-associate row state (which
                    // checkbox is ticked) when the list reorders after a move.
                    <Fragment key={r._id}>
                      <tr className="border-t align-top">
                        {status === 'pending' && (
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selected.has(r._id)} onChange={() => toggle(r._id)} />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <Badge className="bg-primary/10 text-primary">{r.provider}</Badge>
                          {r.category && (
                            <span className={cn('ml-1 inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold', CATEGORY_TONE[r.category.key] || CATEGORY_TONE.other)}>
                              {r.category.label}{r.category.detail ? ` · ${r.category.detail}` : ''}
                            </span>
                          )}
                          <div className="mt-1 font-mono text-[10px] text-muted-foreground">{r.providerRecordId}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.normalized.name || '(no name)'}</div>
                          {r.existing && (
                            <div
                              className="mt-0.5 inline-flex max-w-xs items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900"
                              title={r.existing.latest
                                ? `Already in the CRM: ${r.existing.records} record(s), ${r.existing.open} open. Latest: ${r.existing.latest.name} — ${r.existing.latest.ownerName || 'no owner'}, ${stageLabel(r.existing.latest.stage)}. Assigning gives the chosen person a NEW lead; the earlier record is not changed.`
                                : 'This phone is already a contact in the CRM.'}
                            >
                              Already exists
                              {r.existing.latest
                                ? <span className="truncate font-normal">· {r.existing.latest.ownerName || 'no owner'} · {stageLabel(r.existing.latest.stage)}{r.existing.records > 1 ? ` · ${r.existing.records} records` : ''}</span>
                                : <span className="font-normal">· contact</span>}
                            </div>
                          )}
                          {r.normalized.company && <div className="text-xs text-muted-foreground">{r.normalized.company}</div>}
                          <div className="text-xs">{r.normalized.phone}{r.normalized.altPhone ? ` · ${r.normalized.altPhone}` : ''}</div>
                          {r.normalized.email && <div className="text-xs text-muted-foreground">{r.normalized.email}</div>}
                          {(r.normalized.city || r.normalized.state) && (
                            <div className="text-xs text-muted-foreground">
                              {[r.normalized.city, r.normalized.state].filter(Boolean).join(', ')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>{r.normalized.productInterest || '—'}</div>
                          {r.normalized.message && (
                            <div className="mt-1 max-w-sm text-xs text-muted-foreground">{r.normalized.message}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {new Date(r.normalized.receivedAt || r.createdAt).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            <Button size="sm" variant="ghost"
                              onClick={() => setExpanded(expanded === r._id ? null : r._id)}>
                              Raw
                            </Button>
                            {r.isMoved ? (
                              <Badge className="bg-success/10 text-success-text">Moved</Badge>
                            ) : r.discarded ? (
                              <Badge className="bg-muted text-muted-foreground">Discarded</Badge>
                            ) : (
                              <>
                                {r.assignedTo ? (
                                  <>
                                    <Badge className="bg-info/10 text-info-text">
                                      {r.assignedTo._id === currentUser?.id ? 'Assigned to you' : `Assigned: ${r.assignedTo.display_name || '—'}`}
                                    </Badge>
                                    {(r.assignedTo._id === currentUser?.id || leads.data?.isAdmin) && (
                                      <Button size="sm" variant="ghost" title="Release claim"
                                        disabled={unassign.isPending}
                                        onClick={() => unassign.mutate(r._id)}>
                                        <UserMinus className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <Button size="sm" variant="outline" title="Assign to me"
                                    disabled={assign.isPending}
                                    onClick={() => assign.mutate(r._id)}>
                                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign to me
                                  </Button>
                                )}
                                <Button size="sm" variant="secondary"
                                  disabled={move.isPending}
                                  onClick={() => moveOne(r)}>
                                  Move <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-destructive"
                                  title="Discard" aria-label="Discard"
                                  disabled={discard.isPending}
                                  onClick={() => {
                                    // One stray click must not throw away a real enquiry.
                                    if (window.confirm('Discard this enquiry? It moves to "Discarded" and will not become a pipeline record.')) discard.mutate(r._id);
                                  }}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expanded === r._id && (
                        <tr key={`${r._id}-raw`} className="border-t bg-muted/20">
                          <td colSpan={status === 'pending' ? 6 : 5} className="px-4 py-3">
                            {/* The untouched provider payload — the only way to tell a
                                mapping bug from a genuinely sparse enquiry. */}
                            <pre className="max-h-64 overflow-auto rounded bg-surface p-3 text-[11px]">
                              {JSON.stringify(r.raw ?? {}, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Inbox className="h-3.5 w-3.5" />
        Connect or configure sources in
        <a className="inline-flex items-center gap-0.5 text-primary hover:underline" href="/settings/integrations">
          Settings → Integrations <ExternalLink className="h-3 w-3" />
        </a>
      </p>
    </div>
  );
}
